import type { FullAddress } from "@src/appV2/lib/address/types";

function buildStreetLine(address: FullAddress): string {
  const streetParts = [address.streetNumber, address.streetName].filter(Boolean);
  const street = streetParts.join(" ");

  const unitNumber = address.line1?.trim() || address.subpremise?.trim();
  return unitNumber ? `${street}, ${unitNumber}` : street;
}

function buildLocationLine(address: FullAddress): string {
  const country = address.country === "United States" ? "USA" : address.country;
  const parts = [address.city, address.stateCode, address.postalCode, country];
  return parts.filter(Boolean).join(", ");
}

function parseFormattedAddress(formatted: string): string[] {
  const parts = formatted.split(",").map((part) => part.trim());
  const [firstLine] = parts;
  const remainingLines = parts.slice(1).join(", ");

  return [firstLine, remainingLines].filter(Boolean);
}

/**
 * Formats an address into an array of display lines
 *
 * @param address - The address object to format
 * @returns Array of address lines (street + unit, city/state/zip/country)
 *
 * @example
 * getFormattedAddressLines(address)
 * // Returns: ["123 Main St, Apt 4B", "San Francisco, CA, 94102, USA"]
 *
 * getFormattedAddressLines(undefined)
 * // Returns: ["No address set"]
 */
export function getFormattedAddressLines(address: FullAddress | undefined): string[] {
  if (!address) {
    return ["No address set"];
  }

  const streetLine = buildStreetLine(address);
  const locationLine = buildLocationLine(address);

  if (streetLine && locationLine) {
    return [streetLine, locationLine];
  }

  if (!address.formatted) {
    return ["No address set"];
  }

  return parseFormattedAddress(address.formatted);
}
